import {
  SupportedRentableSpaceTypes,
  RentableSpace,
  SpaceToSubmit,
  Space as LegacySpace,
  SupportedSpaceTypes,
  RentableSpaceWithEstarRequirements,
} from "../../../../../global_functions/fortyTwoApi/tenantsAndSpaces/types";
import { RentableSpaceFormType, SpaceFormType } from "./types";
import { LegacyFloorWithSpaces } from "../../../../../global_functions/fortyTwoApi/tenantsAndSpaces/hooks/useGetFloors";
import { useCurrentUser } from "@src/context/UserContext";
import { supportedRentableSpaceTypes } from "./supportedRentableSpaceTypes";
import { percentErrorCalculations } from "@src/straps/atoms/PercentBreakdown/PercentBreakdown";

// total rentable space area can differ from the rentable floor area within a 1% difference
export const rentableAreaPercentError = 1;

const getFloorCumulativeRentableSpaceArea = (floor: LegacyFloorWithSpaces) =>
  floor.spaces.reduce(
    (acc, space) => (isRentableSpace(space) ? acc + space.floorArea : acc),
    0
  );

/* 
tenants and spaces is completed when all non parking and non roof floors have a rentable floor area assigned (even if 0 SF),
and all rentable floor area is accounted for with rentable spaces, within 1% Error 
*/
export function hasBuildingCompletedTenantsAndSpaces(
  floors?: LegacyFloorWithSpaces[]
) {
  if (!floors) {
    return;
  }
  // parking floors and roofs cannot have rentable spaces, so rentable floor area is not required
  const rentableFloors = floors.filter(
    (floor) => !floor.isRoof && !floor.isParkingFloor
  );

  const needsFurtherData = rentableFloors.some((floor) => {
    if (
      floor.rentableFloorArea === null ||
      floor.rentableFloorArea === undefined
    ) {
      return true;
    }
    const cumulativeSpaceArea = getFloorCumulativeRentableSpaceArea(floor);
    const rentableFloorAreaLowerBound =
      percentErrorCalculations.calculateLowerBound(
        floor.rentableFloorArea,
        rentableAreaPercentError
      );
    const rentableFloorAreaUpperBound =
      percentErrorCalculations.calculateUpperBound(
        floor.rentableFloorArea,
        rentableAreaPercentError
      );
    return (
      rentableFloorAreaLowerBound > cumulativeSpaceArea ||
      rentableFloorAreaUpperBound < cumulativeSpaceArea
    );
  });

  return !needsFurtherData;
}

// constrains rentable area inputs to be within range 0 - 999999
export const rentableAreaInputRegex = "^[0-9]{0,6}$";

export const spaceTypeOptions: {
  id: SupportedRentableSpaceTypes | "CommonArea" | "Parking";
  label: string;
  spaceTypeLabel: string;
}[] = [
  {
    id: "Office",
    label: "Office",
    spaceTypeLabel: "Rentable Space",
  },
  {
    id: "BankBranch",
    label: "Bank Branch",
    spaceTypeLabel: "Rentable Space",
  },
  {
    id: "DataCenter",
    label: "Data Center",
    spaceTypeLabel: "Rentable Space",
  },
  {
    id: "Education",
    label: "Education",
    spaceTypeLabel: "Rentable Space",
  },
  {
    id: "FinancialOffice",
    label: "Financial Office",
    spaceTypeLabel: "Rentable Space",
  },
  {
    id: "Hotel",
    label: "Hotel",
    spaceTypeLabel: "Rentable Space",
  },
  {
    id: "MedicalOffice",
    label: "Medical Office",
    spaceTypeLabel: "Rentable Space",
  },
  {
    id: "Restaurant",
    label: "Restaurant/Fast Food",
    spaceTypeLabel: "Rentable Space",
  },
  {
    id: "RetailStore",
    label: "Retail Store",
    spaceTypeLabel: "Rentable Space",
  },
  {
    id: "Storage",
    label: "Storage",
    spaceTypeLabel: "Rentable Space",
  },
  {
    id: "Other",
    label: "Other",
    spaceTypeLabel: "Rentable Space",
  },
  {
    id: "CommonArea",
    label: "Common Area",
    spaceTypeLabel: "Non-Rentable Space",
  },
  {
    id: "Parking",
    label: "Parking",
    spaceTypeLabel: "Non-Rentable Space",
  },
];

export const tenantsAndSpacesColorMap: Record<
  SupportedSpaceTypes | "Other" | "Leased" | "Unleased",
  `#${string}`
> = {
  Office: "#6AC1BA",
  BankBranch: "#3AA224",
  DataCenter: "#00607B",
  Education: "#3DA7E2",
  FinancialOffice: "#338079",
  Hotel: "#93D1F4",
  MedicalOffice: "#BCCE29",
  Restaurant: "#FFC700",
  RetailStore: "#6DD557",
  Storage: "#D6ECF8",
  Other: "#6C6C6C",
  CommonArea: "#BBD1CF",
  Parking: "#003E42",
  Leased: "#6AC1BA",
  Unleased: "#BBBBBB",
};

// Builds the submit body for a rentable space, or null when required fields are
// missing. Extracted from generateSpaceTypesToSubmit to keep its complexity low.
function generateRentableSpaceToSubmit(
  s: RentableSpaceFormType,
  linkedFloors: Array<string | number>
): SpaceToSubmit | null {
  if (
    !s.spaceType ||
    !s.name ||
    s.isSpaceLeased === undefined ||
    s.floorArea === undefined ||
    (s.isSpaceLeased && !s.tenant) ||
    (!s.isSpaceLeased &&
      (s.isSpaceCooled === null || s.isSpaceCooled === undefined))
  ) {
    return null;
  }
  return {
    ...(s.id && { id: s.id }),
    spaceType: s.spaceType,
    name: s.name,
    floorArea: s.floorArea,
    floors: linkedFloors,
    isSpaceLeased: s.isSpaceLeased,
    leaseStartDate: s.leaseStartDate,
    leaseEndDate: s.leaseEndDate,
    linkedEquipment: s.linkedEquipment,
    ...(s.isSpaceLeased
      ? { tenant: s.tenant }
      : { isSpaceCooled: s.isSpaceCooled }),
  };
}

// returns the request body for creating / updating a space
// return null if required fields are not available in the `SpaceFormType` parameter
export function generateSpaceTypesToSubmit(
  s: SpaceFormType,
  linkedFloors: Array<string | number>
): SpaceToSubmit | null {
  if (linkedFloors.length === 0) {
    return null;
  }
  if (isRentableSpaceFormType(s)) {
    return generateRentableSpaceToSubmit(s, linkedFloors);
  }
  if (s.spaceType === "Parking") {
    if (s.floorArea === undefined) {
      return null;
    }
    return {
      ...(s.id && { id: s.id }),
      spaceType: s.spaceType,
      floorArea: s.floorArea,
      floors: linkedFloors,
      linkedEquipment: s.linkedEquipment,
    };
  }
  if (s.spaceType === "CommonArea") {
    if (!s.name) {
      return null;
    }
    return {
      ...(s.id && { id: s.id }),
      ...(s.floorArea !== undefined && { floorArea: s.floorArea }),
      spaceType: s.spaceType,
      name: s.name,
      floors: linkedFloors,
      linkedEquipment: s.linkedEquipment,
    };
  }
  return null;
}

export function isRentableSpace(space: LegacySpace): space is RentableSpace {
  return supportedRentableSpaceTypes.some((s) => s === space.spaceType);
}
export const isRentableSpaceFormType = (
  space: SpaceFormType
): space is RentableSpaceFormType => {
  if (!space.spaceType) {
    return false;
  }
  return supportedRentableSpaceTypes.some((s) => s === space.spaceType);
};
export function isRentableSpaceWithEstarRequirements(
  space: LegacySpace
): space is RentableSpaceWithEstarRequirements {
  return supportedRentableSpaceTypes.some((s) => s === space.spaceType);
}

export function useCanEditPropertyPages() {
  const { superAdmin } = useCurrentUser();
  return !!superAdmin;
}
